/** Administration for the office: accounts (Admin and Cashier), role defaults, office settings, print agents and the
 *  activity log (Admin). The server checks the role and permissions on every request; this screen only mirrors them. */
import type { AdminUser, DeviceInfo, OfficeSettings, Perms, Role } from '@imagesync/core';
import { GUEST_BLOCKED } from '@imagesync/core';
import { del, get, post, put } from './api.js';
import { canAdmin, isAdmin, office, S } from './state.js';
import { $, $$, $maybe, DOT, esc, fmtDate, fmtDateTime, num, openDialog, closeDialog, plural, setErr, toast } from './util.js';
import { restoreView, viewState } from './viewer.js';

const ROLES: Role[] = ['Admin', 'Cashier', 'Staff', 'Public'];
const PERM_GROUPS: { label: string; items: [keyof Perms, string, string][] }[] = [
  { label: 'Viewing', items: [['originals', 'Originals and redaction', 'See unredacted originals, draw redactions and save rotation'], ['allCaseTypes', 'All case types', 'Otherwise public case types only, so guardianship records stay hidden'],
    ['restrictedBooks', 'Restricted books', 'Open books marked restricted'], ['grayscale', 'Open pages in grayscale', '']] },
  { label: 'Editing', items: [['editRecords', 'Edit index records', 'Correct estate record details'], ['importDocs', 'Import documents', 'Upload, check and import scanned pages'], ['deleteRecords', 'Delete records', 'Remove records and their pages']] },
  { label: 'Copies', items: [['printers', '', ''], ['pdf', 'Downloads', 'PDFs of pages and CSV exports of search results'], ['coverPage', 'Cover page on copies', ''], ['email', 'Email documents', 'Send documents by email']] },
];
const FLAGS = PERM_GROUPS.flatMap(g => g.items.map(i => i[0])).filter(k => k !== 'printers') as Exclude<keyof Perms, 'printers'>[];
const STATUS_LABEL = { active: 'Active', locked: 'Locked', inactive: 'Deactivated' } as const;
const STATUS_PILL = { active: 'ok', locked: 'warn', inactive: '' } as const;

const A = {
  open: false, tab: 'users' as 'users' | 'defaults' | 'settings' | 'devices' | 'activity', q: '', role: '', status: '', page: 1,
  users: [] as AdminUser[], total: 0, editId: null as string | null, draft: null as (AdminUser & { isNew?: boolean }) | null,
  defaults: null as Record<Role, Perms> | null, rdRole: 'Public' as Role, settings: null as OfficeSettings | null, devices: [] as (DeviceInfo & { meta: { version?: string; host?: string } })[],
  log: [] as { id: number; at: string; actor: string; kind: string; text: string }[], logNext: null as number | null, logKind: '', logQ: '',
  view: null as ReturnType<typeof viewState>,
};
let hooks = { afterSelfChange: async () => undefined as void };
const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong.');

export const isAdminOpen = () => A.open;
export const adminTab = () => A.tab;
const me = () => S.me!.user!;

export function openAdmin(): void {
  if (!canAdmin()) { toast('Only Admin and Cashier accounts can manage the office.', 'err'); return; }
  A.open = true;
  A.view = viewState();
  $('.work').hidden = true;
  $('#adminView').hidden = false;
  $('#usersBtn').setAttribute('aria-pressed', 'true');
  if (!isAdmin() && A.tab !== 'users' && A.tab !== 'activity') A.tab = 'users';
  render();
  void loadTab();
  $('#adminTitle').focus();
}
export function closeAdmin(force = false): boolean {
  if (!A.open) return true;
  if (!force && editorDirty()) { toast('Save or cancel your changes first.'); return false; }
  A.open = false; A.editId = null; A.draft = null;
  $('#adminView').hidden = true;
  $('.work').hidden = false;
  $('#usersBtn').setAttribute('aria-pressed', 'false');
  const v = A.view; A.view = null;
  setTimeout(() => restoreView(v), 150);   // once the viewer has its size back
  return true;
}

function render(): void {
  const admin = isAdmin();
  $('#adminTitle').textContent = 'Administration';
  $('#adminSub').textContent = office().name;
  $('#newUserBtn').textContent = admin ? 'New account' : 'New public account';
  for (const t of ['defaults', 'settings', 'devices'] as const) $('#atab-' + t).hidden = !admin;
  $('#adminNote').textContent = admin ? 'Changes here take effect at once, and every one is recorded under Activity.'
    : 'Cashiers manage public accounts only: create them, unlock them, send password resets and set their copy options.';
  const roles = admin ? ROLES : ['Public'];
  if (!roles.includes(A.role as Role)) A.role = '';
  $('#auRole').innerHTML = '<option value="">All roles</option>' + roles.map(r => '<option' + (r === A.role ? ' selected' : '') + '>' + r + '</option>').join('');
  $('#auRole').closest<HTMLElement>('.field')!.hidden = !admin;
  ($('#auSearch') as HTMLInputElement).value = A.q;
  ($('#auStatus') as HTMLSelectElement).value = A.status;
  renderTabs();
}
function renderTabs(): void {
  for (const t of ['users', 'defaults', 'settings', 'devices', 'activity'] as const) {
    const on = A.tab === t, b = $('#atab-' + t);
    b.setAttribute('aria-selected', String(on));
    b.tabIndex = on ? 0 : -1;
    $('#ap-' + t).hidden = !on;
  }
  $('#newUserBtn').hidden = A.tab !== 'users';
}
async function loadTab(): Promise<void> {
  try {
    if (A.tab === 'users') await loadUsers();
    else if (A.tab === 'defaults') { A.defaults = await get<Record<Role, Perms>>('/api/v1/admin/role-defaults'); renderDefaults(); }
    else if (A.tab === 'settings') { A.settings = await get<OfficeSettings>('/api/v1/admin/settings'); A.devices = (await get<{ devices: typeof A.devices }>('/api/v1/admin/devices')).devices; renderSettings(); }
    else if (A.tab === 'devices') { A.devices = (await get<{ devices: typeof A.devices }>('/api/v1/admin/devices')).devices; renderDevices(); }
    else await loadLog(false);
  } catch (e) { toast(errMsg(e), 'err'); }
}

/* ------------------------------------------------------------ accounts */
let searchTimer: ReturnType<typeof setTimeout> | undefined;
async function loadUsers(): Promise<void> {
  const p = new URLSearchParams();
  if (A.q.trim()) p.set('q', A.q.trim());
  if (A.role) p.set('role', A.role);
  if (A.status) p.set('status', A.status);
  p.set('page', String(A.page));
  const r = await get<{ total: number; users: AdminUser[] }>('/api/v1/admin/users?' + p.toString());
  A.users = r.users; A.total = r.total;
  renderUserTable(); renderEditor();
}
const displayName = (u: AdminUser) => (u.kind === 'guest' ? 'Guest terminal' : `${u.first} ${u.last}`.trim() || u.username);
const statusText = (u: AdminUser) => (u.kind === 'guest' ? (u.status === 'active' ? 'Guest login on' : 'Guest login off') : STATUS_LABEL[u.status]);
function accessSummary(u: AdminUser): string {
  const p = u.perms, bits: string[] = [];
  if (p.originals) bits.push('Originals');
  if (p.allCaseTypes) bits.push('All case types');
  if (p.editRecords) bits.push('Edits records');
  if (p.importDocs) bits.push('Imports');
  const n = p.printers.filter(id => office().printers.some(x => x.id === id)).length;
  bits.push(n ? n + ' ' + plural(n, 'printer') : 'No printing');
  if (p.pdf) bits.push('Downloads');
  if (p.grayscale) bits.push('Grayscale');
  return bits.join(' · ');
}
function renderUserTable(): void {
  $('#auCount').textContent = num(A.total) + ' ' + plural(A.total, 'account') + (A.total > A.users.length ? ' (first ' + A.users.length + ' shown; search to narrow)' : '');
  $('#auBody').innerHTML = A.users.length ? A.users.map(u => '<tr data-uid="' + esc(u.id) + '" tabindex="0"' + (A.editId === u.id ? ' aria-current="true"' : '') + '>' +
    '<td><span class="au-name">' + esc(displayName(u)) + '</span>' + (u.id === me().id ? ' <span class="pill">You</span>' : '') +
    '<span class="au-meta"><span class="mono">' + esc(u.username) + '</span>' + (u.org ? ' ' + DOT + ' ' + esc(u.org) : '') + '</span></td>' +
    '<td data-label="Role">' + esc(u.kind === 'guest' ? 'Public (kiosk)' : u.role) + '</td>' +
    '<td data-label="Status"><span class="pill ' + STATUS_PILL[u.status] + '">' + esc(statusText(u)) + '</span></td>' +
    '<td class="nw" data-label="Last sign-in">' + esc(u.lastSignIn ? fmtDate(u.lastSignIn) : 'Never') + '</td>' +
    '<td class="au-access" data-label="Access">' + esc(accessSummary(u)) + '</td></tr>').join('')
    : '<tr><td colspan="5" class="au-empty">No accounts match. Clear the search or filters to see them all.</td></tr>';
  $('#auLayout').classList.toggle('editing', !!A.editId);
}
const permBox = (id: string, attr: string, on: boolean, label: string, hint: string, disabled = false) =>
  '<label class="perm" for="' + id + '"><input type="checkbox" id="' + id + '" ' + attr + (on ? ' checked' : '') + (disabled ? ' disabled' : '') + '><span>' + esc(label) + '</span>' + (hint ? '<small>' + esc(hint) + '</small>' : '') + '</label>';
const CASHIER_OK = new Set(['pdf', 'coverPage', 'email', 'printers']);
function permFieldsHTML(prefix: string, p: Perms, limited = false, guest = false): string {
  return PERM_GROUPS.map(g => '<fieldset class="perm-group"><legend>' + esc(g.label) + '</legend>' + g.items.map(([k, label, hint]) => {
    if (k === 'printers') return office().printers.map((pr, i) => permBox(prefix + '-pr-' + pr.id, 'data-printer="' + esc(pr.id) + '"', p.printers.includes(pr.id), pr.label, i === 0 ? 'Counter printing' : '')).join('') || '<p class="hint">No counter printers are set up. Add them under Office settings.</p>';
    const never = guest && (GUEST_BLOCKED as string[]).includes(k);   // the guest terminal is anyone at the counter
    return permBox(prefix + '-' + k, 'data-perm="' + k + '"', !never && !!p[k], label, never ? 'Not for the guest terminal' : hint, never || (limited && !CASHIER_OK.has(k)));
  }).join('') + '</fieldset>').join('');
}
function readPerms(root: ParentNode, base: Perms): Perms {
  const p = { ...base } as Perms;
  for (const k of FLAGS) { const el = root.querySelector<HTMLInputElement>('[data-perm="' + k + '"]'); if (el) (p as Record<string, unknown>)[k] = el.checked; }
  p.printers = $$<HTMLInputElement>('[data-printer]', root).filter(el => el.checked).map(el => el.dataset.printer!);
  return p;
}
function blankUser(roleName: Role): AdminUser & { isNew: boolean } {
  const perms = A.defaults?.[roleName] ?? ({ originals: false, allCaseTypes: false, restrictedBooks: false, grayscale: false, editRecords: false, importDocs: false, deleteRecords: false, pdf: true, coverPage: true, email: false, printers: [] } as Perms);
  return { id: '', username: '', first: '', last: '', email: '', org: '', role: roleName, kind: 'user', status: 'active', lastSignIn: null, createdAt: new Date().toISOString(), source: null, failed: 0, perms: { ...perms, printers: [...perms.printers] }, note: null, isNew: true };
}
async function openEditor(id: string): Promise<void> {
  if (A.editId && A.editId !== id && editorDirty()) { toast('Save or cancel your changes first.'); return; }
  if (id === 'new') {
    if (!A.defaults && isAdmin()) A.defaults = await get<Record<Role, Perms>>('/api/v1/admin/role-defaults').catch(() => null);
    A.editId = 'new'; A.draft = blankUser('Public');
  } else {
    const u = A.users.find(x => x.id === id);
    if (!u) return;
    A.editId = id; A.draft = structuredClone(u);
  }
  renderUserTable(); renderEditor();
  const ed = $('#auEditor');
  ed.scrollTop = 0;
  if (window.matchMedia?.('(max-width: 1180px)').matches) ed.scrollIntoView({ block: 'start' });
  ed.querySelector<HTMLElement>('input:not([disabled]), select:not([disabled])')?.focus({ preventScroll: true });
}
function closeEditor(): void { A.editId = null; A.draft = null; renderUserTable(); renderEditor(); }
function readEditor(): (AdminUser & { isNew?: boolean }) | null {
  const f = $maybe('#edForm');
  if (!f || !A.draft) return null;
  const d = structuredClone(A.draft), v = (id: string) => $maybe<HTMLInputElement>('#' + id)?.value.trim() ?? null;
  if ($maybe('#ed-username')) d.username = (v('ed-username') ?? '').toLowerCase();
  if ($maybe('#ed-first')) { d.first = v('ed-first') ?? ''; d.last = v('ed-last') ?? ''; d.org = v('ed-org') ?? ''; d.email = v('ed-email') ?? ''; }
  const roleSel = $maybe<HTMLSelectElement>('#ed-role');
  if (roleSel && !roleSel.disabled) d.role = roleSel.value as Role;
  d.perms = readPerms(f, d.perms);
  return d;
}
const editable = (u: AdminUser) => JSON.stringify({ first: u.first, last: u.last, org: u.org ?? '', email: u.email ?? '', role: u.role, perms: { ...u.perms, printers: [...u.perms.printers].sort() } });
function editorDirty(): boolean {
  if (!A.editId) return false;
  const d = readEditor();
  if (!d) return false;
  if (d.isNew) return !!(d.first || d.last || d.username || d.email || d.org);
  const u = A.users.find(x => x.id === A.editId);
  return !!u && editable(d) !== editable(u);
}
function renderEditor(): void {
  const ed = $('#auEditor'), d = A.draft;
  if (!A.editId || !d) { ed.hidden = true; ed.innerHTML = ''; return; }
  const admin = isAdmin(), isNew = !!d.isNew, guest = d.kind === 'guest', self = !isNew && d.id === me().id;
  const saved = isNew ? null : A.users.find(x => x.id === d.id) ?? null;
  const roles = admin ? ROLES : ['Public'];
  const roleLocked = guest || self || !admin;
  const input = (id: string, label: string, val: string | null, extra = '') => '<label class="field" for="' + id + '"><span class="label">' + label + '</span><input type="text" id="' + id + '" value="' + esc(val ?? '') + '"' + extra + '></label>';
  let h = '<div class="ed-head"><div><h3 class="ed-title" id="edTitle">' + (isNew ? (admin ? 'New account' : 'New public account') : esc(displayName(d))) + '</h3>' +
    '<p class="ed-sub">' + (isNew ? 'They get an email with a link to choose their password.'
      : '<span class="mono">' + esc(d.username) + '</span> ' + DOT + ' ' + (d.source ? esc(d.source) + ' ' + DOT + ' ' : '') + 'since ' + esc(fmtDate(d.createdAt))) + '</p></div>' +
    '<button type="button" class="btn small ghost" data-ed="cancel">Close</button></div>';
  if (saved) {
    h += '<div class="ed-status"><span class="pill ' + STATUS_PILL[saved.status] + '">' + esc(statusText(saved)) + '</span>';
    if (!guest) {
      if (saved.status === 'locked') h += '<button type="button" class="btn small" data-ed="unlock">Unlock</button>';
      if (saved.status !== 'inactive') {
        h += '<button type="button" class="btn small" data-ed="reset">Send password link</button>';
        h += '<button type="button" class="btn small" data-ed="deactivate"' + (self ? ' disabled title="You can’t deactivate your own account"' : '') + '>Deactivate</button>';
      } else h += '<button type="button" class="btn small" data-ed="reactivate">Reactivate</button>';
    }
    h += '</div>';
    if (guest) h += '<p class="hint">The guest terminal is turned on and off, and limited to the office’s own computers, under Office settings.</p>';
    if (saved.note && (guest || saved.status === 'locked')) h += '<p class="hint">' + esc(saved.note) + '</p>';
    if (saved.status === 'locked' && saved.failed) h += '<p class="hint">Locked after ' + saved.failed + ' failed sign-ins.</p>';
    if (self) h += '<p class="ed-you">This is you. Another administrator has to change your role or deactivate your account.</p>';
  }
  h += '<form id="edForm" class="stack" novalidate>';
  if (isNew) h += input('ed-username', 'Username', d.username, ' autocomplete="off" spellcheck="false" autocapitalize="none"');
  if (!guest) {
    h += '<div class="row2">' + input('ed-first', 'First name', d.first, ' autocomplete="off"') + input('ed-last', 'Last name', d.last, ' autocomplete="off"') + '</div>';
    h += input('ed-org', 'Organization', d.org, ' autocomplete="off" placeholder="Optional"');
    h += input('ed-email', 'Email', d.email, ' autocomplete="off" spellcheck="false" inputmode="email"');
  }
  h += '<label class="field" for="ed-role"><span class="label">Role</span><select id="ed-role"' + (roleLocked ? ' disabled' : '') + '>' +
    (guest ? '<option value="Public">Public (kiosk)</option>' : roles.map(r => '<option' + (r === d.role ? ' selected' : '') + '>' + r + '</option>').join('')) + '</select></label>';
  if (saved && d.role !== saved.role) h += '<p class="hint">Changing the role keeps this account’s permissions. Choose Use ' + esc(d.role) + ' defaults to switch to that role’s settings.</p>';
  h += '<div class="perm-grid">' + permFieldsHTML('ed', d.perms, !admin, guest) + '</div>';
  h += '<p class="err" id="edErr" hidden></p>';
  h += '<div class="actions"><button type="submit" class="btn primary">' + (isNew ? 'Create account' : 'Save changes') + '</button>' +
    '<button type="button" class="btn" data-ed="cancel">Cancel</button>' +
    (admin ? '<button type="button" class="btn ghost" data-ed="defaults">Use ' + esc(d.role) + ' defaults</button>' : '') + '</div>';
  h += '</form>';
  ed.innerHTML = h;
  ed.hidden = false;
}
async function saveEditor(): Promise<void> {
  const d = readEditor(), err = $('#edErr');
  if (!d) return;
  if (d.kind !== 'guest') {
    if (d.isNew && !/^[a-z0-9._-]{3,60}$/.test(d.username)) { setErr(err, 'Usernames are 3 to 60 characters: lowercase letters, numbers, dots, dashes or underscores.'); return; }
    if (!d.first || !d.last) { setErr(err, 'Enter a first and last name.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email ?? '')) { setErr(err, 'Enter a valid email address. Password links go there.'); return; }
  }
  setErr(err, '');
  try {
    let u: AdminUser;
    if (d.isNew) {
      u = await post<AdminUser>('/api/v1/admin/users', { username: d.username, first: d.first, last: d.last, email: d.email, org: d.org ?? '', role: d.role, perms: d.perms });
      toast('Account created. An email with a link to choose a password went to ' + u.email + '.');
    } else {
      u = await put<AdminUser>('/api/v1/admin/users/' + d.id, { first: d.first || 'Guest', last: d.last || 'terminal', email: d.kind === 'guest' ? null : d.email, org: d.org ?? '', role: d.role, perms: d.perms });
      toast('Saved ' + displayName(u) + '.');
    }
    const i = A.users.findIndex(x => x.id === u.id);
    if (i >= 0) A.users[i] = u; else { A.users.unshift(u); A.total++; }
    A.editId = u.id; A.draft = structuredClone(u);
    renderUserTable(); renderEditor();
    if (u.id === me().id) await hooks.afterSelfChange();
  } catch (e) { setErr(err, errMsg(e)); }
}
async function editorAction(act: string): Promise<void> {
  if (act === 'cancel') { closeEditor(); return; }
  if (act === 'defaults') {
    const x = readEditor();
    if (!x) return;
    if (!A.defaults) A.defaults = await get<Record<Role, Perms>>('/api/v1/admin/role-defaults');
    x.perms = structuredClone(A.defaults[x.role]);
    A.draft = x; renderEditor();
    toast('Filled in the ' + x.role + ' defaults. Save to keep them.');
    return;
  }
  const u = A.users.find(x => x.id === A.editId);
  if (!u) return;
  const keep = readEditor();
  try {
    const r = await post<AdminUser>(`/api/v1/admin/users/${u.id}/${act}`);
    Object.assign(u, r);
    if (keep) { keep.status = r.status; keep.failed = r.failed; A.draft = keep; }
    toast(act === 'unlock' ? displayName(u) + ' can sign in again.' : act === 'deactivate' ? displayName(u) + ' can no longer sign in, and is signed out everywhere. Their history is kept.'
      : act === 'reactivate' ? displayName(u) + ' can sign in again.' : 'A link to choose a password went to ' + u.email + '.');
    renderUserTable(); renderEditor();
  } catch (e) { toast(errMsg(e), 'err'); }
}

/* ------------------------------------------------------------ role defaults */
function renderDefaults(): void {
  if (!A.defaults) return;
  $('#rdRole').innerHTML = ROLES.map(r => '<option' + (r === A.rdRole ? ' selected' : '') + '>' + r + '</option>').join('');
  $('#rdPerms').innerHTML = permFieldsHTML('rd', A.defaults[A.rdRole]);
}
async function saveDefaults(): Promise<void> {
  if (!A.defaults) return;
  const p = readPerms($('#rdForm'), A.defaults[A.rdRole]);
  try {
    A.defaults = await put<Record<Role, Perms>>('/api/v1/admin/role-defaults/' + A.rdRole, p);
    toast('Saved. New ' + A.rdRole + ' accounts start with these settings.');
    renderDefaults();
  } catch (e) { toast(errMsg(e), 'err'); }
}

/* ------------------------------------------------------------ office settings */
function printerRowHTML(p: OfficeSettings['printers'][number] | null, i: number): string {
  const agents = A.devices.filter(d => d.kind === 'print-agent' && !d.revoked);
  return '<div class="printer-row" data-row="' + i + '">' +
    '<label class="field"><span class="label">Id</span><input type="text" data-f="id" class="mono" value="' + esc(p?.id ?? '') + '"' + (p ? ' readonly' : '') + ' placeholder="front"></label>' +
    '<label class="field"><span class="label">Name</span><input type="text" data-f="label" value="' + esc(p?.label ?? '') + '" placeholder="Front counter printer"></label>' +
    '<label class="field"><span class="label">Short name</span><input type="text" data-f="short" value="' + esc(p?.short ?? '') + '" placeholder="front printer"></label>' +
    '<label class="field"><span class="label">Printer on the computer</span><input type="text" data-f="systemName" value="' + esc(p?.systemName ?? '') + '" placeholder="HP LaserJet 4001"></label>' +
    '<label class="field"><span class="label">Print agent</span><select data-f="deviceId"><option value="">None yet</option>' + agents.map(a => '<option value="' + esc(a.id) + '"' + (p?.deviceId === a.id ? ' selected' : '') + '>' + esc(a.name) + '</option>').join('') + '</select></label>' +
    '<button type="button" class="btn small" data-remove-printer="' + i + '">Remove</button></div>';
}
function renderSettings(): void {
  const s = A.settings;
  if (!s) return;
  const agents = A.devices.filter(d => d.kind === 'print-agent' && !d.revoked);
  $('#setForm').innerHTML =
    '<fieldset><legend>Contact details</legend><p class="hint">Shown on the sign-in screen and in Help.</p>' +
    '<div class="row2"><label class="field" for="stDesk"><span class="label">Desk</span><input type="text" id="stDesk" value="' + esc(s.contact.desk) + '"></label><label class="field" for="stPhone"><span class="label">Phone</span><input type="text" id="stPhone" value="' + esc(s.contact.phone) + '"></label></div>' +
    '<div class="row2"><label class="field" for="stEmail"><span class="label">Email</span><input type="text" id="stEmail" value="' + esc(s.contact.email) + '"></label><label class="field" for="stHours"><span class="label">Hours</span><input type="text" id="stHours" value="' + esc(s.contact.hours) + '"></label></div></fieldset>' +
    '<fieldset><legend>Copies</legend><label class="field" for="stFee" style="max-width:220px"><span class="label">Copy fee a page (dollars)</span><input type="text" id="stFee" class="mono" value="' + (s.feeCents / 100).toFixed(2) + '" inputmode="decimal"></label><p class="hint">Shown before printing and on the cover page. Copies are paid for at the counter.</p></fieldset>' +
    '<fieldset><legend>Guest terminal and sign-in</legend>' +
    '<label class="switch" for="stGuest"><input type="checkbox" id="stGuest"' + (s.guestLogin ? ' checked' : '') + '> Offer the guest terminal on the office’s own computers</label>' +
    '<label class="field" for="stNets"><span class="label">Office networks for the guest terminal</span><textarea id="stNets" spellcheck="false" placeholder="203.0.113.10 or 203.0.113.0/28, one a line">' + esc(s.kioskNetworks.join('\n')) + '</textarea></label>' +
    '<p class="hint">The addresses the office’s public computers use on the internet. The guest option only appears there.</p>' +
    '<label class="switch" for="stReg"><input type="checkbox" id="stReg"' + (s.registration ? ' checked' : '') + '> Let the public register for an account online</label>' +
    '<label class="field" for="stLock" style="max-width:260px"><span class="label">Wrong passwords before an account locks</span><input type="text" id="stLock" class="mono" value="' + s.lockoutAttempts + '" inputmode="numeric"></label></fieldset>' +
    '<fieldset><legend>Counter printers</legend><div id="stPrinters" class="stack">' + s.printers.map((p, i) => printerRowHTML(p, i)).join('') + '</div>' +
    '<div class="actions"><button type="button" class="btn small" id="stAddPrinter">Add a printer</button></div>' +
    (agents.length ? '' : '<p class="hint">Register a print agent under Print agents to connect printers.</p>') + '</fieldset>' +
    '<p class="err" id="stErr" hidden></p><div class="actions"><button type="submit" class="btn primary">Save settings</button><button type="button" class="btn" id="stUndo">Undo changes</button></div>';
}
function readSettings(): OfficeSettings | string {
  const v = (id: string) => $<HTMLInputElement>('#' + id).value.trim();
  const fee = Number(v('stFee').replace(/^\$/, ''));
  if (!Number.isFinite(fee) || fee < 0 || fee > 1000) return 'Enter the copy fee in dollars, such as 0.50.';
  const lock = Number(v('stLock'));
  if (!Number.isInteger(lock) || lock < 3 || lock > 20) return 'Lock accounts after 3 to 20 wrong passwords.';
  const printers = $$('#stPrinters .printer-row').map(r => {
    const f = (k: string) => (r.querySelector<HTMLInputElement>('[data-f="' + k + '"]')!.value ?? '').trim();
    return { id: f('id').toLowerCase(), label: f('label'), short: f('short') || f('label'), systemName: f('systemName'), deviceId: f('deviceId') || null };
  }).filter(p => p.id || p.label);
  for (const p of printers) {
    if (!/^[a-z0-9-]{1,40}$/.test(p.id)) return 'Printer ids are short lowercase words, such as front or records-room.';
    if (!p.label) return 'Give each printer a name.';
  }
  return { contact: { desk: v('stDesk'), phone: v('stPhone'), email: v('stEmail'), hours: v('stHours') }, feeCents: Math.round(fee * 100), guestLogin: $<HTMLInputElement>('#stGuest').checked,
    kioskNetworks: v('stNets').split(/[\s,]+/).filter(Boolean), registration: $<HTMLInputElement>('#stReg').checked, lockoutAttempts: lock, printers };
}
async function saveSettings(): Promise<void> {
  const err = $('#stErr'), v = readSettings();
  if (typeof v === 'string') { setErr(err, v); return; }
  setErr(err, '');
  try {
    A.settings = await put<OfficeSettings>('/api/v1/admin/settings', v);
    renderSettings();
    toast('Settings saved.');
    await hooks.afterSelfChange();   // printers, fee and contact show in the records screen too
  } catch (e) { setErr(err, errMsg(e)); }
}

/* ------------------------------------------------------------ print agents */
function renderDevices(): void {
  const list = A.devices.filter(d => d.kind === 'print-agent');
  $('#devList').innerHTML = list.length ? list.map(d => {
    const printers = (A.settings?.printers ?? []).filter(p => p.deviceId === d.id).map(p => p.label);
    return '<li class="dev"><span class="dn">' + esc(d.name) + (d.revoked ? ' <span class="pill">Removed</span>' : '') + '</span>' +
      (d.revoked ? '<span></span>' : '<button type="button" class="btn small" data-remove-device="' + esc(d.id) + '">Remove</button>') +
      '<span class="dm">' + (d.lastSeen ? 'Last checked in ' + esc(fmtDateTime(d.lastSeen)) : 'Hasn’t checked in yet') + (d.meta?.host ? ' ' + DOT + ' ' + esc(d.meta.host) : '') + (d.meta?.version ? ' ' + DOT + ' version ' + esc(d.meta.version) : '') +
      (printers.length ? ' ' + DOT + ' prints on ' + esc(printers.join(', ')) : '') + '</span></li>';
  }).join('') : '<li class="empty">No print agents yet.</li>';
}
async function registerDevice(): Promise<void> {
  const name = $<HTMLInputElement>('#devName').value.trim(), err = $('#devErr');
  if (!name) { setErr(err, 'Enter a name for the computer, such as Front counter PC.'); return; }
  setErr(err, '');
  try {
    const r = await post<{ device: DeviceInfo; token: string; server: string }>('/api/v1/admin/devices', { name, kind: 'print-agent' });
    $<HTMLInputElement>('#devName').value = '';
    $('#tokenBody').innerHTML = '<p>Install the print agent on <b>' + esc(r.device.name) + '</b> with this server address and token. The token is shown only now; if it’s lost, remove this agent and register it again.</p>' +
      '<span class="label">Server</span><div class="token-box">' + esc(r.server || location.origin) + '</div><span class="label">Token</span><div class="token-box" id="tokenValue">' + esc(r.token) + '</div>' +
      '<div class="actions"><button type="button" class="btn small" id="copyToken">Copy the token</button></div><p class="hint">Then choose the agent for each printer under Office settings.</p>';
    openDialog($<HTMLDialogElement>('#tokenDlg'));
    A.devices = (await get<{ devices: typeof A.devices }>('/api/v1/admin/devices')).devices;
    renderDevices();
  } catch (e) { setErr(err, errMsg(e)); }
}

/* ------------------------------------------------------------ activity */
async function loadLog(more: boolean): Promise<void> {
  const p = new URLSearchParams();
  if (A.logKind) p.set('kind', A.logKind);
  if (A.logQ.trim()) p.set('q', A.logQ.trim());
  if (more && A.logNext) p.set('before', String(A.logNext));
  const r = await get<{ entries: typeof A.log; next: number | null }>('/api/v1/admin/audit?' + p.toString());
  A.log = more ? [...A.log, ...r.entries] : r.entries;
  A.logNext = r.next;
  renderLog();
}
function renderLog(): void {
  $('#alKind').closest<HTMLElement>('.field')!.hidden = !isAdmin();
  $('#adminLog').innerHTML = A.log.length ? A.log.map(a => '<li><time>' + esc(fmtDateTime(a.at)) + '</time><span>' + esc(a.text) + ' ' + DOT + ' ' + esc(a.actor) + '</span></li>').join('')
    : '<li class="empty">Nothing yet.</li>';
  $('#alMore').hidden = !A.logNext;
}

export function wireAdmin(h: typeof hooks): void {
  hooks = h;
  $('#closeAdmin').addEventListener('click', () => closeAdmin());
  $('#newUserBtn').addEventListener('click', () => void openEditor('new'));
  $('#adminTabs').addEventListener('click', e => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-atab]');
    if (!b || b.hidden) return;
    if (A.tab === 'users' && editorDirty()) { toast('Save or cancel your changes first.'); return; }
    A.tab = b.dataset.atab as typeof A.tab; renderTabs(); void loadTab();
  });
  $('#adminTabs').addEventListener('keydown', e => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    const tabs = (['users', 'defaults', 'settings', 'devices', 'activity'] as const).filter(t => !$('#atab-' + t).hidden);
    let i = tabs.indexOf(A.tab as (typeof tabs)[number]);
    i = (i + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    A.tab = tabs[i]!; renderTabs(); void loadTab(); $('#atab-' + tabs[i]).focus(); e.preventDefault();
  });
  $('#auSearch').addEventListener('input', e => { A.q = (e.target as HTMLInputElement).value; clearTimeout(searchTimer); searchTimer = setTimeout(() => void loadUsers(), 250); });
  $('#auRole').addEventListener('change', e => { A.role = (e.target as HTMLSelectElement).value; void loadUsers(); });
  $('#auStatus').addEventListener('change', e => { A.status = (e.target as HTMLSelectElement).value; void loadUsers(); });
  $('#auBody').addEventListener('click', e => { const tr = (e.target as HTMLElement).closest<HTMLElement>('tr[data-uid]'); if (tr) void openEditor(tr.dataset.uid!); });
  $('#auBody').addEventListener('keydown', e => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const tr = (e.target as HTMLElement).closest<HTMLElement>('tr[data-uid]');
    if (tr) { e.preventDefault(); void openEditor(tr.dataset.uid!); }
  });
  $('#auEditor').addEventListener('click', e => { const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-ed]'); if (b && !b.disabled) void editorAction(b.dataset.ed!); });
  $('#auEditor').addEventListener('submit', e => { if ((e.target as HTMLElement).id === 'edForm') { e.preventDefault(); void saveEditor(); } });
  $('#auEditor').addEventListener('change', e => {
    if ((e.target as HTMLElement).id !== 'ed-role') return;
    const d = readEditor();
    if (!d) return;
    if (d.isNew && A.defaults) d.perms = structuredClone(A.defaults[d.role]);
    A.draft = d; renderEditor();
    $maybe('#ed-role')?.focus();
  });
  $('#rdRole').addEventListener('change', e => { A.rdRole = (e.target as HTMLSelectElement).value as Role; renderDefaults(); });
  $('#rdForm').addEventListener('submit', e => { e.preventDefault(); void saveDefaults(); });
  $('#rdReset').addEventListener('click', () => { renderDefaults(); toast('Changes undone.'); });
  $('#setForm').addEventListener('submit', e => { e.preventDefault(); void saveSettings(); });
  $('#setForm').addEventListener('click', e => {
    const t = e.target as HTMLElement;
    if (t.id === 'stUndo') { renderSettings(); toast('Changes undone.'); }
    if (t.id === 'stAddPrinter') {
      const box = $('#stPrinters');
      box.insertAdjacentHTML('beforeend', printerRowHTML(null, box.children.length));
      $$<HTMLInputElement>('#stPrinters .printer-row:last-child input')[0]?.focus();
    }
    const rm = t.closest<HTMLElement>('[data-remove-printer]');
    if (rm) { rm.closest('.printer-row')!.remove(); }
  });
  $('#devForm').addEventListener('submit', e => { e.preventDefault(); void registerDevice(); });
  $('#devList').addEventListener('click', async e => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-remove-device]');
    if (!b) return;
    if (b.dataset.armed !== '1') { b.dataset.armed = '1'; b.textContent = 'Click again to remove'; setTimeout(() => { b.dataset.armed = ''; b.textContent = 'Remove'; }, 4000); return; }
    try { await del('/api/v1/admin/devices/' + b.dataset.removeDevice); toast('Removed. Its token no longer works.'); A.devices = (await get<{ devices: typeof A.devices }>('/api/v1/admin/devices')).devices; renderDevices(); }
    catch (err) { toast(errMsg(err), 'err'); }
  });
  $('#tokenDlg').addEventListener('click', e => {
    const t = e.target as HTMLElement;
    if (t.closest('[data-close]')) closeDialog($<HTMLDialogElement>('#tokenDlg'));
    if (t.id === 'copyToken') void navigator.clipboard?.writeText($('#tokenValue').textContent ?? '').then(() => toast('Token copied.'), () => toast('Select the token and copy it.'));
  });
  $('#alKind').addEventListener('change', e => { A.logKind = (e.target as HTMLSelectElement).value; void loadLog(false); });
  let t: ReturnType<typeof setTimeout> | undefined;
  $('#alQ').addEventListener('input', e => { A.logQ = (e.target as HTMLInputElement).value; clearTimeout(t); t = setTimeout(() => void loadLog(false), 300); });
  $('#alMore').addEventListener('click', () => void loadLog(true));
}
